import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3, TrendingUp, MapPin, Users, Calendar, Music2,
  DollarSign, Award, Loader2, ArrowUp, ArrowDown, Minus
} from "lucide-react";
import { format, parseISO, startOfYear, endOfYear, startOfMonth, subMonths, getMonth, getYear } from "date-fns";
import { Layout } from "@/pages/Dashboard";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

export const CareerStats = () => {
  const [loading, setLoading] = useState(true);
  const [gigs, setGigs] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [timeRange, setTimeRange] = useState("all"); // all, year, 6months
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gigsRes, collabRes] = await Promise.all([
        apiClient.get("/gigs?time_filter=all"),
        apiClient.get("/collaborators")
      ]);
      setGigs(gigsRes.data || []);
      setCollaborators(collabRes.data || []);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter gigs by time range
  const getFilteredGigs = () => {
    const now = new Date();
    return gigs.filter(gig => {
      const gigDate = parseISO(gig.date);
      if (timeRange === "year") {
        return getYear(gigDate) === selectedYear;
      } else if (timeRange === "6months") {
        return gigDate >= subMonths(now, 6);
      }
      return true; // all
    });
  };
  
  const filteredGigs = getFilteredGigs();
  const pastGigs = filteredGigs.filter(g => parseISO(g.date) < new Date());
  const upcomingGigs = filteredGigs.filter(g => parseISO(g.date) >= new Date());
  
  // Calculate stats
  const totalGigs = pastGigs.length;
  const totalRevenue = pastGigs.reduce((sum, g) => sum + (parseFloat(g.fee) || 0), 0);
  const paidGigs = pastGigs.filter(g => g.fee_status === "paid");
  const pendingRevenue = pastGigs.filter(g => g.fee && g.fee_status !== "paid")
    .reduce((sum, g) => sum + (parseFloat(g.fee) || 0), 0);
  const avgFee = totalGigs > 0 ? totalRevenue / pastGigs.filter(g => g.fee).length : 0;
  
  // Venue stats
  const venueCount = {};
  pastGigs.forEach(g => {
    const venue = g.venue_name || "Unknown";
    venueCount[venue] = (venueCount[venue] || 0) + 1;
  });
  const topVenues = Object.entries(venueCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  // City stats
  const cityCount = {};
  pastGigs.forEach(g => {
    const city = g.city || "Unknown";
    cityCount[city] = (cityCount[city] || 0) + 1;
  });
  const topCities = Object.entries(cityCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  // Genre stats
  const genreCount = {};
  pastGigs.forEach(g => {
    const genre = g.genre || "Other";
    genreCount[genre] = (genreCount[genre] || 0) + 1;
  });
  const topGenres = Object.entries(genreCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  // Monthly breakdown
  const monthlyGigs = {};
  const monthlyRevenue = {};
  pastGigs.forEach(g => {
    const date = parseISO(g.date);
    const key = format(date, "MMM yyyy");
    monthlyGigs[key] = (monthlyGigs[key] || 0) + 1;
    monthlyRevenue[key] = (monthlyRevenue[key] || 0) + (parseFloat(g.fee) || 0);
  });
  
  // Get last 12 months for chart
  const last12Months = [];
  for (let i = 11; i >= 0; i--) {
    const date = subMonths(new Date(), i);
    const key = format(date, "MMM yyyy");
    last12Months.push({
      month: format(date, "MMM"),
      fullKey: key,
      gigs: monthlyGigs[key] || 0,
      revenue: monthlyRevenue[key] || 0
    });
  }
  
  // Busiest month calculation
  const monthTotals = {};
  pastGigs.forEach(g => {
    const month = getMonth(parseISO(g.date));
    monthTotals[month] = (monthTotals[month] || 0) + 1;
  });
  const busiestMonthNum = Object.entries(monthTotals)
    .sort((a, b) => b[1] - a[1])[0];
  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  const busiestMonth = busiestMonthNum ? monthNames[parseInt(busiestMonthNum[0])] : "N/A";
  
  // Gig type breakdown
  const gigTypeCount = {};
  pastGigs.forEach(g => {
    const type = g.gig_type || "public";
    gigTypeCount[type] = (gigTypeCount[type] || 0) + 1;
  });
  
  // Available years for filter
  const years = [...new Set(gigs.map(g => getYear(parseISO(g.date))))].sort((a, b) => b - a);
  
  // Calculate trend (compare to previous period)
  const getTrend = (current, previous) => {
    if (previous === 0) return current > 0 ? "up" : "neutral";
    const change = ((current - previous) / previous) * 100;
    if (change > 5) return "up";
    if (change < -5) return "down";
    return "neutral";
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
    <div className="space-y-6">
      {/* Header — DS-token kicker + heroSm, matches other page intros */}
      <div style={{ marginBottom: tokens.space.xxl }}>
        <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
          Career Stats
        </Kicker>
        <div
          className="flex flex-row items-center justify-between"
          style={{ gap: tokens.space.md }}
        >
          <h1
            style={{
              margin: 0,
              ...tokens.type.heroSm,
              color: tokens.color.text,
              textWrap: "balance",
              minWidth: 0,
            }}
          >
            Your Stats
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, flexShrink: 0 }}>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]" data-testid="time-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="year">By Year</SelectItem>
              <SelectItem value="6months">Last 6 Months</SelectItem>
            </SelectContent>
          </Select>
          
          {timeRange === "year" && (
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          </div>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="stat-total-gigs">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Gigs</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalGigs}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {upcomingGigs.length} upcoming
            </p>
          </CardContent>
        </Card>
        
        <Card data-testid="stat-total-revenue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Earned</CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-500">
              £{totalRevenue.toLocaleString()}
            </div>
            {pendingRevenue > 0 && (
              <p className="text-xs text-amber-400 mt-1">
                £{pendingRevenue.toLocaleString()} pending
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card data-testid="stat-avg-fee">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average Fee</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              £{avgFee ? Math.round(avgFee).toLocaleString() : 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              per paid gig
            </p>
          </CardContent>
        </Card>
        
        <Card data-testid="stat-busiest-month">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Busiest Month</CardTitle>
            <Award className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{busiestMonth}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {busiestMonthNum ? `${busiestMonthNum[1]} gigs` : "No data"}
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Monthly Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Activity</CardTitle>
          <CardDescription>Gigs and revenue over the last 12 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-end gap-1 sm:gap-2">
            {last12Months.map((month, i) => {
              const maxGigs = Math.max(...last12Months.map(m => m.gigs), 1);
              const height = (month.gigs / maxGigs) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div
                    className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${month.month}: ${month.gigs} gigs, £${month.revenue}`}
                  />
                  {/* Show every 2nd label on mobile to prevent overflow */}
                  <span className={`text-muted-foreground truncate w-full text-center${i % 2 !== 0 ? " hidden sm:block" : ""}`}
                    style={{ fontSize: '9px' }}>
                    {month.month}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-primary" />
              <span className="text-muted-foreground">Gigs per month</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Detailed Stats */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Venues */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Top Venues
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topVenues.length > 0 ? (
              <div className="space-y-3">
                {topVenues.map(([venue, count], i) => (
                  <div key={venue} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-muted-foreground w-6">
                        {i + 1}.
                      </span>
                      <span className="font-medium truncate max-w-[200px]">{venue}</span>
                    </div>
                    <Badge variant="secondary">{count} gigs</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No venue data yet</p>
            )}
          </CardContent>
        </Card>
        
        {/* Top Cities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Top Cities
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topCities.length > 0 ? (
              <div className="space-y-3">
                {topCities.map(([city, count], i) => (
                  <div key={city} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-muted-foreground w-6">
                        {i + 1}.
                      </span>
                      <span className="font-medium">{city}</span>
                    </div>
                    <Badge variant="secondary">{count} gigs</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No city data yet</p>
            )}
          </CardContent>
        </Card>
        
        {/* Genre Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music2 className="h-5 w-5 text-primary" />
              Genre Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topGenres.length > 0 ? (
              <div className="space-y-3">
                {topGenres.map(([genre, count]) => {
                  const percentage = Math.round((count / totalGigs) * 100);
                  return (
                    <div key={genre} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="capitalize">{genre}</span>
                        <span className="text-muted-foreground">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No genre data yet</p>
            )}
          </CardContent>
        </Card>
        
        {/* Gig Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Gig Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(gigTypeCount).map(([type, count]) => (
                <div key={type} className="bg-secondary/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-sm text-muted-foreground capitalize">{type}</div>
                </div>
              ))}
              {Object.keys(gigTypeCount).length === 0 && (
                <p className="text-muted-foreground col-span-2 text-center py-4">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Mileage Log moved to the Money hub (/money -> Mileage tab) */}

      {/* Revenue by Month Table */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Breakdown</CardTitle>
          <CardDescription>Monthly earnings summary</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">Month</th>
                  <th className="text-center py-2 font-medium">Gigs</th>
                  <th className="text-right py-2 font-medium">Revenue</th>
                  <th className="text-right py-2 font-medium">Avg/Gig</th>
                </tr>
              </thead>
              <tbody>
                {last12Months.filter(m => m.gigs > 0).reverse().map((month, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2">{month.fullKey}</td>
                    <td className="py-2 text-center">{month.gigs}</td>
                    <td className="py-2 text-right text-emerald-400">
                      £{month.revenue.toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-muted-foreground">
                      £{month.gigs > 0 ? Math.round(month.revenue / month.gigs) : 0}
                    </td>
                  </tr>
                ))}
                {last12Months.filter(m => m.gigs > 0).length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      No gig data in the last 12 months
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
};

export default CareerStats;
